import type { DeepMaybeRef, MaybeRef } from "@vueuse/core";
import type { ComputedRef, Ref } from "vue";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { ValidationTestName } from "./validationTestName";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";

import { computed, unref, ref, toValue } from "vue";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";

import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";

import type {
  Correlations,
  Equipment,
  Form,
  OperatingParameters,
  Scenario,
  WellProperties
} from "../forms/formTypes";
import type { Maybe } from "graphql/jsutils/Maybe";
import { generateAllValidationRules } from "./rules/allValidations";
import { MIN_CLEARANCE } from "./validatorConstants";
import { correlationChecks } from "./rules/correlationChecks";
import type { StackLocation } from "@/components/equipment/d";

export enum Severity {
  "success",
  "info",
  "warn",
  "error",
  "catastrophe"
}

const equipmentOrderValues: { [key in EquipmentCalculatorSectionKey]: number } =
  {
    [EquipmentCalculatorSectionKey.OPERATING_PARAMETERS]: 1,
    [EquipmentCalculatorSectionKey.INTAKE_CONDITIONS]: 2,
    [EquipmentCalculatorSectionKey.PUMP]: 3,
    [EquipmentCalculatorSectionKey.MOTOR]: 4,
    [EquipmentCalculatorSectionKey.SEAL]: 5,
    [EquipmentCalculatorSectionKey.MLE]: 6,
    [EquipmentCalculatorSectionKey.CABLE]: 7,
    [EquipmentCalculatorSectionKey.SURFACE_DRIVE]: 8,
    [EquipmentCalculatorSectionKey.WELL_COMPLETION]: 9,
    [EquipmentCalculatorSectionKey.WELL_TEST]: 10,
    [EquipmentCalculatorSectionKey.FLUID_PROPERTIES]: 11,
    [EquipmentCalculatorSectionKey.GAS_PROPERTIES]: 12,
    [EquipmentCalculatorSectionKey.THRUST_CHAMBER]: 13,
    [EquipmentCalculatorSectionKey.SEAL_MOTOR_OIL]: 14,
    [EquipmentCalculatorSectionKey.HPS_MOTOR]: 15
  };

export type ValidationRule = {
  name: ValidationTestName;
  isActive: boolean;
  isInvalid?: boolean;
  message: string;
  severity: Severity;
  rulePlacement: EquipmentCalculatorSectionKey;
  disablesRules?: string[];
  affectedXrefIds?: string[];
  resolver?: (
    parametersForm: Form<NonNullable<Scenario["parameters"]>>
  ) => void;
  // resolver?: (parameters: Ref<Scenario["parameters"]>) => void;
  // resolver?: (parameters: Scenario["parameters"] | undefined) => void;
};

export type UseScenarioValidator = {
  allRules: ComputedRef<ValidationRule[]>;
  correlationRules: ComputedRef<ValidationRule[]>;
  errors: ComputedRef<ValidationRule[]>;
  filterByXrefIdAndRuleNames(
    ruleNames: ValidationTestName[],
    xrefId?: string | null
  ): ComputedRef<ValidationRule[]>;
};

export type ValidationRuleGenerator = (args: {
  equipment: Ref<Maybe<Equipment>>;
  wellProperties: Ref<Maybe<WellProperties>>;
  parameters: Ref<Maybe<OperatingParameters>>;
  scenario: Ref<Maybe<Scenario>>;
  calcs: ScenarioCalculations;
  correlations: Ref<Maybe<Correlations>>;
  stack?: StackLocation;
  resolver?: (
    parametersForm: Form<NonNullable<Scenario["parameters"]>>
  ) => void;
}) => ValidationRule[];

export const hasClearanceViolation = (
  motorShroudInteriorDiameterInches: number,
  actualDiameter: number,
  clearanceDiameter: number,
  depthOffset = 0,
  checkShroud = true
) => {
  if (checkShroud && motorShroudInteriorDiameterInches > 0) {
    clearanceDiameter = motorShroudInteriorDiameterInches;
  }
  return clearanceDiameter - actualDiameter - depthOffset < MIN_CLEARANCE;
};

export function valuesExist(values: MaybeRef<number | string>[]): boolean {
  return values
    .map(val => unref(val))
    .every(val => {
      if (typeof val === "string") {
        return val !== null && val !== undefined && val !== "";
      } else {
        return val !== null && val !== undefined && !isNaN(val);
      }
    });
}

/**
 * Adds stack prefix to validation message when dual stacks are enabled
 * @param message - Original validation message
 * @param stack - Current stack being validated ("upper" or "lower")
 * @param equipment - Equipment ref to check if dual stacks are enabled
 * @returns Message with stack prefix if dual stacks enabled, otherwise original message
 */
export function addStackPrefixToMessage(
  message: string,
  stack: "upper" | "lower" | undefined,
  equipment: Ref<Maybe<Equipment>>
): string {
  if (!stack) return message;

  const hasLowerStack = unref(equipment)?.has_lower_equip_stack ?? false;
  if (!hasLowerStack) return message;

  const stackLabel = stack === "upper" ? "Upper Stack" : "Lower Stack";
  return `${stackLabel}: ${message}`;
}

export const useScenarioValidator = (
  scenario:
    | ComputedRef<DestructuredScenario>
    | DeepMaybeRef<DestructuredScenario>,
  calcs?: ComputedRef<ScenarioCalculations> | ScenarioCalculations,
  stack?: "upper" | "lower"
): UseScenarioValidator => {
  // Unwrap the scenario reactively to handle ComputedRef
  const scenarioValue = computed(() => toValue(scenario));

  // Unwrap calcs reactively if it's a ComputedRef
  const calcsValue = computed(() => (calcs ? toValue(calcs) : undefined));

  const allRules = computed<ValidationRule[]>(() => {
    const s = scenarioValue.value;
    const currentCalcs = calcsValue.value;
    const scenarioForCalcs = currentCalcs
      ? undefined
      : (scenario as DeepMaybeRef<DestructuredScenario>);
    return generateAllValidationRules({
      equipment: ref(s.equipment),
      wellProperties: ref(s.wellProperties),
      parameters: ref(s.parameters),
      calcs: currentCalcs ?? useScenarioCalculations(scenarioForCalcs ?? s),
      scenario: ref(s.scenario),
      correlations: ref(s.correlations),
      stack
    });
  });

  const correlationRules = computed(() => {
    const s = scenarioValue.value;
    const currentCalcs = calcsValue.value;
    const scenarioForCalcs = currentCalcs
      ? undefined
      : (scenario as DeepMaybeRef<DestructuredScenario>);
    return correlationChecks({
      equipment: ref(s.equipment),
      wellProperties: ref(s.wellProperties),
      parameters: ref(s.parameters),
      calcs: currentCalcs ?? useScenarioCalculations(scenarioForCalcs ?? s),
      scenario: ref(s.scenario),
      correlations: ref(s.correlations),
      stack
    });
  });

  function applyFilters(
    ruleNames?: ValidationTestName[],
    xrefId?: string
  ): ValidationRule[] {
    // get list of rules currently broken
    const currentlyBrokenRules = allRules.value.filter(
      rule => rule.isActive && rule.isInvalid
    );

    const filteredRules = currentlyBrokenRules.filter(rule => {
      // no inputs, let all pass
      if (!xrefId && !ruleNames) return true;

      // no filtering by xref, only filter by name
      if (!rule.affectedXrefIds) return ruleNames?.includes(rule.name);

      // filter by name and xref ids
      return (
        ruleNames?.includes(rule.name) &&
        rule.affectedXrefIds?.includes(xrefId || "")
      );
    });

    // find list of active rules that disable other rules
    const rulesToDisable = filteredRules
      .flatMap(rule => rule.disablesRules)
      .filter(disabledRules => {
        // disabledRules
        return disabledRules;
      });

    // Filtered list of only applicable
    // current errors ordered by severity
    return filteredRules
      .filter(rule => !rulesToDisable.includes(rule.name))
      .sort((a, b) => {
        // Compare severity first
        if (b.severity !== a.severity) {
          return b.severity - a.severity;
        }
        // Then compare equipmentOrder using the mapping
        const aOrder =
          equipmentOrderValues[a.rulePlacement] || Number.MAX_VALUE;
        const bOrder =
          equipmentOrderValues[b.rulePlacement] || Number.MAX_VALUE;
        return aOrder - bOrder;
      });
  }

  return {
    allRules,
    correlationRules,
    errors: computed(() => applyFilters()),
    filterByXrefIdAndRuleNames: (
      ruleNames: ValidationTestName[],
      xrefId?: string
    ) => computed(() => applyFilters(ruleNames, xrefId))
  };
};
